import { useState, type FormEvent } from 'react';

import { KeyRound, Lock } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { HOME_PATH, LOGIN_PATH } from '@/app/router';
import { BrandLockup } from '@/components/brand/BrandLockup';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

import { useInvitation, useSetPassword } from '../api';
import styles from './SetPasswordPage.module.css';

/** Matches the API, which is the authority. Mirrored here only to fail sooner. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * Completes an invitation or a password reset. The mechanism is the same.
 *
 * Public by necessity: the recipient cannot sign in yet, and the single-use token in
 * the link is the credential.
 *
 * The link is checked before the form renders, so an expired or already-used one
 * fails with an explanation rather than after someone has chosen and typed a password
 * twice.
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const invitation = useInvitation(token);
  const setPassword = useSetPassword();

  const [password, setPassword_] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{
    password?: string | undefined;
    confirm?: string | undefined;
  }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const next: typeof errors = {};
    if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (confirm !== password) next.confirm = 'The two passwords do not match.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setPassword.mutate(
      { token, password },
      {
        onSuccess: () => {
          // The API signs them in, so they go straight to the app.
          void navigate(HOME_PATH, { replace: true });
        },
      },
    );
  };

  const invalidLink = !token || (invitation.isError && invitation.error instanceof ApiError);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.mark}>
          <BrandLockup height={54} tone="light" />
        </div>

        {invitation.isPending && token ? (
          <div className={styles.centre}>
            <Spinner size={20} label="Checking your link" />
          </div>
        ) : invalidLink ? (
          <EmptyState
            tone="danger"
            icon={<KeyRound size={21} />}
            title="This link is no longer valid"
            body={
              invitation.error instanceof ApiError
                ? invitation.error.message
                : 'Ask an administrator to send you a new one.'
            }
            action={{
              label: 'Back to sign in',
              onClick: () => {
                void navigate(LOGIN_PATH, { replace: true });
              },
            }}
          />
        ) : (
          <>
            <h1 className={styles.title}>
              {invitation.data?.is_reset ? 'Choose a new password' : 'Set your password'}
            </h1>
            <p className={styles.subtitle}>
              {invitation.data?.is_reset
                ? 'This replaces your current password.'
                : `Welcome, ${invitation.data?.full_name ?? ''}. Choose a password to finish setting up your account.`}
            </p>
            <p className={styles.account}>{invitation.data?.email}</p>

            {setPassword.error instanceof ApiError ? (
              <Alert tone="danger" className={styles.banner}>
                {setPassword.error.message}
              </Alert>
            ) : null}

            <form onSubmit={submit} noValidate>
              <Field
                label="New password"
                required
                error={errors.password}
                hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
              >
                {({ id, describedBy, invalid }) => (
                  <PasswordInput
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    autoComplete="new-password"
                    icon={<Lock size={16} />}
                    value={password}
                    onChange={(event) => {
                      setPassword_(event.target.value);
                      setErrors((current) => ({ ...current, password: undefined }));
                    }}
                  />
                )}
              </Field>

              <Field label="Confirm password" required error={errors.confirm}>
                {({ id, describedBy, invalid }) => (
                  <PasswordInput
                    id={id}
                    describedBy={describedBy}
                    invalid={invalid}
                    autoComplete="new-password"
                    icon={<Lock size={16} />}
                    value={confirm}
                    onChange={(event) => {
                      setConfirm(event.target.value);
                      setErrors((current) => ({ ...current, confirm: undefined }));
                    }}
                  />
                )}
              </Field>

              <Button type="submit" size="lg" block loading={setPassword.isPending}>
                {setPassword.isPending ? 'Saving' : 'Set password and sign in'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
